import { IS_WEB } from "@common/utils/mobileUtils"
import { liveKeyOfAddress } from "@common/navigation/liveAddress"
import { BO_URL } from "@data/constants/apiConstants"

/**
 * Deep-link classification helpers.
 *
 * The runtime entry point is `classifyDeepLink(url, ctx)` below. It returns
 * one of three actions:
 *   - `route`  — let React Navigation handle the URL normally
 *   - `pend`   — store as `pendingDeepLink`, resume after auth/mode resolves
 *   - `ignore` — drop the URL silently
 *
 * Four sources drive the decision:
 *   - WEB_ONLY_PREFIXES   — stay in the browser even on mobile (Stripe etc.)
 *   - AUTH_ONLY_PREFIXES  — only for logged-out users (login, register, ...)
 *   - TUTOR_PATH_PREFIXES — used by auto-bypass + tutor-account guard
 *   - menuScreens         — map menu screen name ↔ menu path
 *
 * Public, parent-supported, and parent-only screens are NOT hand-maintained
 * lists — they're derived at runtime:
 *   - Public  = any `navigationScreens` entry with `auth` in `titles`
 *   - Parent-supported = any entry with `parent` in `titles`
 *   - Parent-only      = a `menuScreens` entry whose tab name starts with
 *                        `parent-` (resolves URLs like `/menu/parent`,
 *                        `/menu/reports` to the parent bottom-tab screens
 *                        that live inside `ParentMainLayout`, not in
 *                        `navigationScreens` directly), or an entry whose
 *                        `titles` name `parent` alone
 *
 * Default behavior for a path not in any list: routes as a logged-in
 * student/main path, flipping out of parent mode if needed. That's the
 * correct default for most screens; only add to the tutor prefix list when
 * you want mode-aware behavior.
 *
 * See `docs/deep-linking.md` for the full workflow,
 * including the Android intent-filter / iOS AASA side of the contract.
 */

const TUTOR_PATH_PREFIXES = [
    "menu/dashboard",
    "menu/students",
    "menu/proposals",
    "menu/schools",
    "menu/tutor-resources",
    "menu/activities-review",
    "menu/tutor-profile",
    "menu/complete-profile",
    "menu/live-tutor-resources",
    // Reached from the dispo confirmation mail: a tutor who also has a family
    // account on the device must land on their tutor account, not the account picker.
    "tutor/ma-dispo",
]

/**
 * Web-only paths: stay in browser, never trigger the app.
 * Uses startsWith so sub-paths also match (except /subscribe which is exact).
 *
 * Whether the OS hands a URL to the app at all is decided elsewhere, by the
 * allowlist in deepLinkClaims.js (absent = browser). This runtime check is
 * the second line: links opened from inside the app, push `path`s, and
 * installs still holding the old wildcard AASA in cache. A path must never be
 * in both lists — deepLinkClaims.test.js enforces it.
 */
export const WEB_ONLY_PREFIXES = [
    "/subscribe/redirect",
    "/subscribe/return",
    "/subscribe/create-subscription",
    "/notifications/unsubscribe",
    "/school-card-report",
    "/invoice-payment",
    "/brevet",
    "/stage",
    "/summer",
    // Funnel d'inscription générique : même raison que ses voisins — le
    // parcours se termine par un checkout Stripe, et ses liens magiques
    // (`?user=`) désignent UN lead, que l'app connectée ne doit pas détourner.
    // NB : distinct de `/register`, l'écran de création de compte, qui lui doit
    // continuer d'ouvrir l'app.
    "/inscription",
    // Landing du lien d'activation. Web-only comme les funnels, et pour une
    // raison de plus : le lien désigne UN élève, alors qu'une app déjà
    // connectée en désigne peut-être un autre — l'ouvrir dans l'app ferait
    // atterrir le parent sur le mauvais enfant.
    "/activation",
]

const matchesPrefix = (p, prefix) =>
    p === prefix || p.startsWith(prefix.endsWith("/") ? prefix : prefix + "/")

// Strip query/hash and force a leading slash so callers can pass either a
// pathname ("/x") or a path-with-params string ("/x?y=1") — the latter is
// what `getPathFromState` returns when route params round-trip into the URL.
const normalizePath = (pathname) => {
    const stripped = pathname.split(/[?#]/)[0]
    return stripped.startsWith("/") ? stripped : "/" + stripped
}

export const isWebOnlyPath = (pathname) => {
    if (!pathname) return false
    const p = normalizePath(pathname)
    if (p === "/subscribe") return true
    return WEB_ONLY_PREFIXES.some((prefix) => matchesPrefix(p, prefix))
}

/**
 * Build a matcher from a screen name. Supports the `:param` dynamic-segment
 * syntax that React Navigation uses (e.g. "referrer/dashboard/:h").
 */
const buildScreenPathMatcher = (name) => {
    if (!name) return null
    if (!name.includes(":")) return (p) => p === name
    const pattern = name.replace(/:[^/]+/g, "[^/]+")
    const regex = new RegExp("^" + pattern + "$")
    return (p) => regex.test(p)
}

/**
 * Screens whose `titles` include the given mode key. Derives the matcher list
 * from navigationScreens so the classifier stays in sync with the actual
 * navigator without a hand-maintained mirror list.
 *
 * Lazy require avoids pulling navigationScreens' component graph into modules
 * that only need classification helpers. Not cached: the matcher list is cheap
 * to rebuild and a stale module-level cache would lie to tests / HMR sessions
 * that mutate navigationScreens.
 */
const getScreenMatchers = (keepTitleKeys) => {
    const {
        navigationScreens,
    } = require("@common/navigation/navigationScreens")
    return (navigationScreens.main ?? [])
        .filter((s) => {
            const keys = (s.titles ?? []).flatMap((t) =>
                typeof t === "object" && t ? Object.keys(t) : []
            )
            return keys.length > 0 && keepTitleKeys(keys)
        })
        .map((s) => buildScreenPathMatcher(s.name))
        .filter(Boolean)
}

const getScreenMatchersWithTitle = (titleKey) =>
    getScreenMatchers((keys) => keys.includes(titleKey))

const normalizeScreenPath = (pathname) => {
    if (!pathname) return null
    const p = pathname.split(/[?#]/)[0].replace(/^\/+/, "")
    return p || null
}

/**
 * Public paths: screens registered in the auth stack, so they render for
 * logged-out users too (email verification, password reset, login, public
 * referral dashboards, prospect trial flows, etc.).
 */
export const isPublicPath = (pathname) => {
    const p = normalizeScreenPath(pathname)
    if (!p) return false
    return getScreenMatchersWithTitle("auth").some((match) => match(p))
}

/**
 * Parent-supported paths: screens registered in the parent stack. Mostly
 * mode-agnostic flows that also render in main/auth (brevet funnel, live
 * reports, subscribe sub-pages, makeup occurrences, etc.). Used by
 * `needsModeSwitch` to keep a parent-mode user from being kicked out when the
 * target already renders in their current navigator.
 */
export const isParentSupportedPath = (pathname) => {
    const p = normalizeScreenPath(pathname)
    if (!p) return false
    return getScreenMatchersWithTitle("parent").some((match) => match(p))
}

/**
 * Auth-only paths: only make sense for logged-out users. A logged-in user
 * landing on these via a deep link should be redirected to their home.
 */
const AUTH_ONLY_PREFIXES = [
    "/login",
    "/register",
    "/forgot-password",
    "/account-choice",
]

export const isAuthOnlyPath = (pathname) => {
    if (!pathname) return false
    const p = normalizePath(pathname)
    return AUTH_ONLY_PREFIXES.some((prefix) => matchesPrefix(p, prefix))
}

/**
 * Parent-only paths: URLs that only render in parent mode. Used to auto-enter
 * parent mode when a notification or an e-mail deep-links there from main mode.
 *
 * Two sources, both derived:
 *   - a parent tab (`menuScreens`): every parent tab name shares the `parent-`
 *     prefix (parent-home, parent-reports, …);
 *   - a screen of the parent stack registered nowhere else — its `titles` name
 *     `parent` alone (the stage programme an e-mail leads to).
 */
export const isParentPath = (path) => {
    if (resolveMenuScreenName(path)?.startsWith("parent-")) return true
    const p = normalizeScreenPath(path)
    if (!p) return false
    return getScreenMatchers((keys) => keys.every((k) => k === "parent")).some(
        (match) => match(p)
    )
}

/**
 * Check if a deep link path targets a tutor-only screen.
 */
export const isTutorPath = (path) =>
    TUTOR_PATH_PREFIXES.some((p) => path?.startsWith(p))

/**
 * True when a deep link path targets a screen whose mode doesn't match the
 * current one.
 *
 * - In parent mode: switch out only when the path is neither parent-only
 *   (a parent tab) nor parent-supported (registered in the parent stack).
 *   Mode-agnostic screens like the brevet funnel or `/r/:id` keep the user
 *   in parent mode instead of forcing a detour through parent home.
 * - In main mode: switch in only when the path is parent-only — a backend
 *   notification deep-link to e.g. `/menu/parent`.
 */
export const needsModeSwitch = (path, isParentMode) => {
    if (isParentMode) return !isParentPath(path) && !isParentSupportedPath(path)
    return isParentPath(path)
}

/**
 * True when a parent-only deep link target must be discarded rather than
 * actioned: entering parent mode while `isLiveMode` is true would strand a
 * student mid-live-session (P-3581 — a "support-chat" reply push or a
 * parent-report e-mail link tapped during the call). `RootNavigator` has no
 * live screen registered in the parent stack to fall back to.
 *
 * Decision of 05/10 (Wajih): discarded, not deferred — the link concerns the
 * parent, so nothing should happen on the student's screen once the live
 * ends either. The only caller clears the pending link immediately instead
 * of leaving it to resolve later.
 */
export const isParentModeSwitchDiscardedByLive = (path, isLiveMode) =>
    !!isLiveMode && isParentPath(path)

/**
 * Join a base URL and a path without creating double slashes. Optionally
 * appends a query string built from a flat object of primitives.
 */
export const buildDeepLinkUrl = (baseUrl, path, params) => {
    const base = `${(baseUrl ?? "").replace(/\/+$/, "")}/${(path ?? "").replace(/^\/+/, "")}`
    if (!params || typeof params !== "object") return base
    const query = Object.entries(params)
        .filter(
            ([, v]) => v !== undefined && v !== null && typeof v !== "object"
        )
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&")
    return query ? `${base}?${query}` : base
}

/**
 * Extract pathname and query params from a deep link URL (HTTP or custom scheme).
 * Returns { path, params } or null for invalid URLs.
 */
export const extractPathFromUrl = (url) => {
    if (!url) return null
    try {
        const parsed = new URL(url)
        return {
            path: parsed.pathname.replace(/^\//, ""),
            params: Object.fromEntries(parsed.searchParams.entries()),
        }
    } catch {
        // Custom scheme URL (masteur:///path?query)
        const match = url.match(/\/\/\/(.*)/)
        if (!match) return null
        const [pathPart, queryPart] = match[1].split("?")
        const params = queryPart
            ? Object.fromEntries(new URLSearchParams(queryPart).entries())
            : {}
        return { path: pathPart, params }
    }
}

/**
 * Extract the pathname from a deep link URL.
 * Returns the path without leading slash, or null for invalid URLs.
 */
export const extractDeepLinkPath = (url) =>
    extractPathFromUrl(url)?.path ?? null

/**
 * Menu screen config: maps screen names to deep link paths.
 * Single source of truth used by App.js linking config and deep link navigation.
 */
export const menuScreens = {
    dashboard: "menu/dashboard",
    schools: "menu/schools",
    "tutor-resources": "menu/tutor-resources",
    students: "menu/students",
    proposals: "menu/proposals",
    "activities-review": "menu/activities-review",
    live: "menu/live",
    "finished-activities-live": "menu/finished-activities-live",
    "live-students": "menu/live-students",
    "live-activities-review": "menu/live-activities-review",
    "live-tutor-resources": "menu/live-tutor-resources",
    home: "menu/home",
    "finished-activities": "menu/finished-activities",
    "skill-sets": "menu/skill-sets",
    rankings: "menu/rankings",
    tutor: "menu/tutor",
    activities: "menu/activities",
    "tutor-profile": "menu/tutor-profile",
    "complete-profile": "menu/complete-profile",
    "parent-home": "menu/parent",
    "parent-activities": "menu/parent-activities",
    "parent-reports": "menu/reports",
    "parent-settings": "menu/parent-settings",
    "parent-subscription": "parent-subscription",
}

// Reverse lookup: deep link path → screen name
const pathToScreenName = new Map(
    Object.entries(menuScreens).map(([screen, path]) => [path, screen])
)

/**
 * Resolve a deep link path to a screen name using the menu screens config.
 * e.g. "menu/reports" → "parent-reports", "menu/dashboard" → "dashboard"
 */
export const resolveMenuScreenName = (path) =>
    pathToScreenName.get(path) ?? null

/**
 * Pages of the former in-app back office that the backend still links to
 * (ops Slack messages, notifications, exports), mapped to their BO v2 page
 * as `[path, query params]`.
 * The back office moved to bo-web; without this, those links would open an
 * empty screen in the app.
 */
const LEGACY_BACKOFFICE_PATHS = new Map([
    [
        "application-details",
        ({ tutorId }) =>
            tutorId
                ? [
                      `tutors/${encodeURIComponent(tutorId)}`,
                      { tab: "application" },
                  ]
                : ["retention/tutor-applications"],
    ],
    [
        "live-show",
        ({ live_id }) =>
            live_id
                ? [`lives/sessions/${encodeURIComponent(live_id)}`]
                : ["lives/sessions"],
    ],
    // BO v2 has no generic user page: the list is the closest landing.
    ["admin-users", () => ["students"]],
    // Older Slack and e-mail links, emitted before the backend moved to BO v2.
    [
        "group-attribution-detail",
        ({ groupAttributionId }) =>
            groupAttributionId
                ? [
                      `lives/attributions/${encodeURIComponent(groupAttributionId)}`,
                  ]
                : ["lives/attributions"],
    ],
    [
        "attribution-tickets",
        ({ id }) => (id ? ["ops", { ticket_id: id }] : ["ops"]),
    ],
    ["lives-history", () => ["lives/occurrences"]],
])

/**
 * The BO v2 URL a former in-app back-office link now points to, or null when
 * the URL is not one of those pages.
 */
export const resolveLegacyBackofficeUrl = (url) => {
    const parsed = extractPathFromUrl(url)
    // Tolerate "/live-show/" and "//live-show": links are concatenated by hand.
    const path = parsed?.path.replace(/^\/+|\/+$/g, "")
    const toBackofficePath = LEGACY_BACKOFFICE_PATHS.get(path)
    if (!toBackofficePath) return null
    const [backofficePath, params] = toBackofficePath(parsed.params)
    return buildDeepLinkUrl(BO_URL, backofficePath, params)
}

/**
 * Classify a deep link URL into an action. Used by both the web mount-time
 * handler and the native linking callbacks to keep the decision tree in one
 * place.
 *
 * Actions:
 *  - "ignore"   — web-only, auth-only while authed, a live address
 *                 (`/live/<key>`), or unparseable. Drop it.
 *  - "route"    — no special handling; let React Navigation route normally.
 *  - "pend"     — store as pendingDeepLink (auth required, or mode switch).
 *  - "external" — a page of the former in-app back office; open `url` (its
 *                 BO v2 page) instead.
 *
 * @param {string} url - The deep link URL to classify
 * @param {object} ctx - { token, isParentMode }
 */
export const classifyDeepLink = (url, ctx = {}) => {
    const path = extractDeepLinkPath(url)
    if (!path) return { action: "ignore" }

    // L'adresse d'un live n'est pas un lien à suivre : au démarrage, la mémoire du live décide
    // seule si cet onglet y entre (`liveMemory`). Ni mise de côté, ni réécrite en `/`.
    if (liveKeyOfAddress(path) != null) return { action: "ignore", path }

    const backofficeUrl = resolveLegacyBackofficeUrl(url)
    if (backofficeUrl) return { action: "external", path, url: backofficeUrl }

    if (isWebOnlyPath(path)) {
        // Authed user in the wrong mode still needs a mode switch before the
        // web-only screen can render — e.g. "/subscribe" is only registered
        // in main/live/auth navigators, so a parent-mode user lands on the
        // parent home instead. Pend so RootNavigator flips the mode first.
        if (ctx.token && needsModeSwitch(path, ctx.isParentMode)) {
            return { action: "pend", path }
        }
        return { action: "ignore", path }
    }

    if (!ctx.token) {
        if (isPublicPath(path)) return { action: "route", path }
        return { action: "pend", path }
    }

    if (isAuthOnlyPath(path)) return { action: "ignore", path }

    if (needsModeSwitch(path, ctx.isParentMode)) {
        return { action: "pend", path }
    }

    return { action: "route", path }
}

/**
 * Navigate to a route or open in a new tab on web
 * @param {object} navigation - React Navigation navigation object
 * @param {string} routeName - The route/screen name to navigate to
 * @param {object} params - Route parameters
 */
export const navigateOrOpenTab = (navigation, routeName, params = {}) => {
    if (IS_WEB) {
        const queryString = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null)
            .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
            .join("&")
        const url = `/${routeName}${queryString ? `?${queryString}` : ""}`
        window.open(url, "_blank")
    } else {
        navigation.push(routeName, params)
    }
}
